import { safeSessionStorage } from '@/lib/storage/safe-storage';
import { chatApiBaseUrl } from '@/lib/website-auth/browser';

import type { ResumableClientStorage } from '@assistant-ui/react-ai-sdk';

/**
 * Window event dispatched by the PRODUCING tab the moment it acquires its stream id, carrying
 * `{streamId, threadUuid}` in `detail`. The cross-tab sync hook re-broadcasts it so sibling tabs
 * on the same thread can tail the live stream. Kept as a window event (not a direct broadcast)
 * because `setStreamId` runs in plain module scope with no BroadcastChannel handle.
 */
export const RESUMABLE_STREAM_SET_EVENT = 'bazak-resumable-stream-set';

/**
 * The stream-discovery URL for a thread (`200 {streamId}` while a run is live, else `204`). Shared
 * by the status feed and the discovery poll so the path/encoding/query stay in lockstep.
 */
export function buildThreadStreamUrl({
  tenantName,
  threadUuid,
  resourceId,
}: {
  tenantName: string;
  threadUuid: string;
  resourceId: string;
}): string {
  return `${chatApiBaseUrl(tenantName)}/${tenantName}/threads/${encodeURIComponent(
    threadUuid,
  )}/stream?resourceId=${encodeURIComponent(resourceId)}`;
}

interface ResumableStreamRecord {
  streamId: string;
  threadUuid: string;
  /**
   * How the client learned the stream id: `header` = the original chat response's
   * `x-resumable-stream-id` header (normal reconnect); `discovery` = the backend stream-discovery
   * endpoint (reload BEFORE headers arrived). Surfaced on the RESPONSE_RESUMED analytics event.
   */
  source?: 'header' | 'discovery';
}

// The durable uuid of the currently ACTIVE (mounted) thread, kept in sync by
// useThreadPersistence. Read-side guard only: a stored stream id is surfaced to the runtime's
// resume-on-mount check exclusively when the mounted thread is the one the stream belongs to,
// so a replay can never be injected into a freshly-created blank thread after a reload.
// Page-scoped state lives on globalThis, NOT module scope: dev Fast Refresh replaces this
// module while the transport keeps the previous closure, splitting the state into two copies
// (writes land in the new one, reads come from the old one) — resume then never arms.
interface ResumablePageState {
  activeThreadUuid: string | null;
  /**
   * Stream ids ACQUIRED by this page load: their original consumer is this very page, so the
   * resume-on-mount check must never replay them (double-consuming a live stream). Only a
   * RELOADED page (fresh state) may resume.
   */
  producedStreamIds: Set<string>;
  /**
   * Epoch ms until which this tab's own thread switches must NOT re-broadcast THREAD_SWITCH. Set
   * while adopting a sibling's stream (the adopting tab is FOLLOWING, not leading) so its
   * `switchToThread` onto the streaming thread doesn't tell the other tabs to follow it back.
   */
  suppressSwitchBroadcastUntil: number;
}

function pageState(): ResumablePageState {
  const g = globalThis as { __bazakResumablePageState?: ResumablePageState };
  return (g.__bazakResumablePageState ??= {
    activeThreadUuid: null,
    producedStreamIds: new Set(),
    suppressSwitchBroadcastUntil: 0,
  });
}

const SWITCH_BROADCAST_SUPPRESS_MS = 2000;

/** Suppress THREAD_SWITCH re-broadcast for the adopting tab's imminent bounce (both switches). */
export function suppressSwitchBroadcast(): void {
  pageState().suppressSwitchBroadcastUntil = Date.now() + SWITCH_BROADCAST_SUPPRESS_MS;
}

export function isSwitchBroadcastSuppressed(): boolean {
  return Date.now() < pageState().suppressSwitchBroadcastUntil;
}

export function setResumableActiveThread(threadUuid: string | null): void {
  const state = pageState();
  state.activeThreadUuid = threadUuid;
}

/**
 * True if THIS page produced the stream (it's the sender, not a sibling). Cross-tab adoption keys
 * off the BroadcastChannel's tab-id self-ignore, but that id is module-scoped and dev Fast Refresh
 * can split it; this globalThis-backed set is the robust guard so a tab never adopts its own stream.
 */
export function hasProducedStream(streamId: string): boolean {
  return pageState().producedStreamIds.has(streamId);
}

/**
 * Resumable-stream-id storage, one record per tenant: `{streamId, threadUuid}`. The write side
 * keys the record to the thread with the in-flight run (the transport's live thread ref, which
 * holds the durable uuid used as `memory.thread`); the read side only returns the id once that
 * same thread is active again — surviving reloads, where the transport ref starts out null.
 */
export function createTenantThreadResumableStorage({
  tenantName,
  currentThreadIdRef,
}: {
  tenantName: string;
  currentThreadIdRef: { readonly current: string | null };
}): ResumableClientStorage {
  const storageKey = `bazak-resumable-stream:${tenantName}`;

  const readRecord = () => getResumableRecord(tenantName);

  return {
    getStreamId: () => {
      const record = readRecord();
      if (!record) return null;
      if (pageState().producedStreamIds.has(record.streamId)) return null;
      const active = currentThreadIdRef.current ?? pageState().activeThreadUuid;
      const match = active === record.threadUuid;
      return match ? record.streamId : null;
    },
    // Consumed by the patched resume check (react-ai-sdk): only the chat instance whose
    // thread owns the record may consume the stream.
    getThreadUuid: () => readRecord()?.threadUuid ?? null,
    setStreamId: (id: string) => {
      const threadUuid = currentThreadIdRef.current;
      if (!threadUuid) return;
      pageState().producedStreamIds.add(id);
      const record: ResumableStreamRecord = { streamId: id, threadUuid, source: 'header' };
      safeSessionStorage.setItem(storageKey, JSON.stringify(record));
      // Announce the live stream to sibling tabs so they can tail it (cross-tab streaming sync).
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(RESUMABLE_STREAM_SET_EVENT, { detail: { streamId: id, threadUuid } }),
        );
      }
    },
    clear: () => {
      safeSessionStorage.removeItem(storageKey);
    },
  };
}

/**
 * Primes the resume record from server-side stream DISCOVERY (reload-during-send: the response
 * header carrying the stream id never arrived, but the backend can look the live stream up by
 * thread). Deliberately NOT marked as produced-by-this-page — the whole point is to resume it.
 */
export function primeResumableRecord({
  tenantName,
  streamId,
  threadUuid,
}: {
  tenantName: string;
  streamId: string;
  threadUuid: string;
}): void {
  const record: ResumableStreamRecord = { streamId, threadUuid, source: 'discovery' };
  safeSessionStorage.setItem(`bazak-resumable-stream:${tenantName}`, JSON.stringify(record));
}

/** The pending resumable record (stream id + owning thread), if any. */
export function getResumableRecord(tenantName: string): ResumableStreamRecord | null {
  const storageKey = `bazak-resumable-stream:${tenantName}`;
  const raw = safeSessionStorage.getItem(storageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ResumableStreamRecord>;
    if (typeof parsed.streamId === 'string' && typeof parsed.threadUuid === 'string') {
      return parsed as ResumableStreamRecord;
    }
  } catch {
    // fall through to cleanup
  }
  // Corrupt / wrong-shape record — drop it so it can't wedge future reads.
  safeSessionStorage.removeItem(storageKey);
  return null;
}

export function clearResumableRecord(tenantName: string): void {
  safeSessionStorage.removeItem(`bazak-resumable-stream:${tenantName}`);
}

export function hasResumableRecordForThread({
  tenantName,
  threadUuid,
}: {
  tenantName: string;
  threadUuid: string;
}): boolean {
  return getResumableRecord(tenantName)?.threadUuid === threadUuid;
}

/** The thread the pending resumable stream belongs to, if any (used to restore it after reload). */
export function getResumableThreadUuid(tenantName: string): string | null {
  return getResumableRecord(tenantName)?.threadUuid ?? null;
}

const interruptedKey = (tenantName: string, threadUuid: string) =>
  `bazak-resumable-interrupted:${tenantName}:${threadUuid}`;

/**
 * Records the moment a pending response was interrupted (pagehide) so the resume half of the
 * funnel can report interrupt→resume latency. sessionStorage survives a same-tab reload, so the
 * value is still readable when the reloaded page re-attaches the stream.
 */
export function markResponseInterrupted({
  tenantName,
  threadUuid,
}: {
  tenantName: string;
  threadUuid: string;
}): void {
  safeSessionStorage.setItem(interruptedKey(tenantName, threadUuid), String(Date.now()));
}

/** Elapsed ms since this thread's interruption, cleared on read. Undefined when none recorded. */
export function consumeInterruptionElapsedMs({
  tenantName,
  threadUuid,
}: {
  tenantName: string;
  threadUuid: string;
}): number | undefined {
  const key = interruptedKey(tenantName, threadUuid);
  const raw = safeSessionStorage.getItem(key);
  if (raw === null) return undefined;
  safeSessionStorage.removeItem(key);
  const at = Number(raw);
  return Number.isFinite(at) ? Math.max(0, Date.now() - at) : undefined;
}
